import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, Search, Edit2, Trash2, X, Upload,
    FileSpreadsheet, Filter, CheckCircle, XCircle, Grid, Layers
} from "lucide-react";

import {
    fetchDomainesRequest,
    fetchThemesRequest,
    fetchThemesByDomaineRequest,
    createDomaineRequest,
    updateDomaineRequest,
    deleteDomaineRequest,
    createThemeRequest,
    updateThemeRequest,
    deleteThemeRequest,
    downloadDomainesTemplateRequest,
    downloadThemesTemplateRequest,
    importDomainesExcelRequest,
    importThemesExcelRequest,
    clearBulkReview,
    clearParamDomainesThemesError,
} from "../../../../redux/actions/paramDomainesThemesActions";

import { API_BASE_URL } from "../../../../../api";
import "../RessourcesStyles/ParamDomainesThemes.css";

/* ─────────────────────────────────────────────
   États initiaux des formulaires
───────────────────────────────────────────── */
const emptyDomaineForm = {
    reference: "",
    nom: "",
    description: "",
    couleur: "#2f4595",
    actif: true,
    imageFile: null,
};

const emptyThemeForm = {
    reference: "",
    nom: "",
    description: "",
    actif: true,
    domaineId: "",
};

/* ─────────────────────────────────────────────
   Modal générique
───────────────────────────────────────────── */
const Modal = ({ open, title, onClose, footer, children }) => {
    if (!open) return null;
    return (
        <div className="pdt-modal" onMouseDown={onClose}>
            <div className="pdt-modal__panel" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className="pdt-modal__header">
                    <div className="pdt-modal__title">{title}</div>
                    <button className="pdt-icon-btn" onClick={onClose} type="button" aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="pdt-modal__body">
                    {children}
                    {footer && <div className="pdt-modal__footer">{footer}</div>}
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   Composant principal
───────────────────────────────────────────── */
export default function ParamDomainesThemes() {
    const dispatch = useDispatch();

    const {
        domaines = [],
        themes = [],
        themesFiltered = [],
        loading,
        error,
        bulkLoading,
        bulkResult,
        bulkError,
    } = useSelector((state) => state.paramDomainesThemes || {});

    const [tab, setTab] = useState("domaines");
    const [q, setQ] = useState("");
    const [selectedDomaineId, setSelectedDomaineId] = useState("");

    // Modals
    const [openDomaineModal, setOpenDomaineModal] = useState(false);
    const [openThemeModal, setOpenThemeModal]     = useState(false);
    const [openBulkModal, setOpenBulkModal]       = useState(false);

    // Bulk
    const [bulkType, setBulkType] = useState("DOMAINES");
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkFileName, setBulkFileName] = useState("");

    // Forms
    const [editingDomaine, setEditingDomaine] = useState(null);
    const [editingTheme, setEditingTheme]     = useState(null);
    const [domaineForm, setDomaineForm]       = useState(emptyDomaineForm);
    const [themeForm, setThemeForm]           = useState(emptyThemeForm);
    const [imagePreview, setImagePreview]     = useState(null);

    const tabs = useMemo(() => [
        { id: "domaines", label: "Domaines",  icon: Grid },
        { id: "themes",   label: "Thèmes",                icon: Layers },
    ], []);

    useEffect(() => {
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            Swal.fire({
                icon: "error", title: "Erreur",
                text: typeof error === "string" ? error : "Une erreur est survenue",
                confirmButtonColor: "#2f4595",
            }).then(() => dispatch(clearParamDomainesThemesError()));
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (bulkError) {
            Swal.fire({
                icon: "error", title: "Erreur import",
                text: typeof bulkError === "string" ? bulkError : "Une erreur est survenue",
                confirmButtonColor: "#2f4595",
            });
        }
    }, [bulkError]);

    const themesToUse = useMemo(() => {
        if (Array.isArray(themesFiltered) && themesFiltered.length) return themesFiltered;
        return Array.isArray(themes) ? themes : [];
    }, [themesFiltered, themes]);

    /* ── Filtrage ── */
    const filteredDomaines = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        if (!t) return domaines;
        return domaines.filter((d) =>
            `${d.reference} ${d.nom} ${d.description}`.toLowerCase().includes(t)
        );
    }, [domaines, q]);

    const filteredThemes = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        let list = themesToUse;
        if (selectedDomaineId) list = list.filter((x) => String(x.domaineId) === String(selectedDomaineId));
        if (!t) return list;
        return list.filter((th) =>
            `${th.reference} ${th.nom} ${th.description} ${th.domaineNom}`.toLowerCase().includes(t)
        );
    }, [themesToUse, q, selectedDomaineId]);

    /* ── Handlers Domaine ── */
    const openCreateDomaine = () => {
        setEditingDomaine(null);
        setDomaineForm(emptyDomaineForm);
        setImagePreview(null);
        setOpenDomaineModal(true);
    };

    const openEditDomaine = (d) => {
        setEditingDomaine(d);
        setDomaineForm({
            reference:   d.reference  || "",
            nom:         d.nom        || "",
            description: d.description|| "",
            couleur:     d.couleur    || "#2f4595",
            actif:       d.actif      ?? true,
            imageFile:   null,
        });
        setImagePreview(d.image ? `${API_BASE_URL}/api/domaines/images/${d.image}` : null);
        setOpenDomaineModal(true);
    };

    const closeDomaineModal = () => {
        setOpenDomaineModal(false);
        setEditingDomaine(null);
        setDomaineForm(emptyDomaineForm);
        setImagePreview(null);
    };

    const onChangeDomaine = (e) => {
        const { name, value, type, checked } = e.target;
        setDomaineForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    };

    const onPickImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setDomaineForm((p) => ({ ...p, imageFile: file }));
        setImagePreview(URL.createObjectURL(file));
    };

    const submitDomaine = (e) => {
        e.preventDefault();
        if (!domaineForm.reference?.trim()) { Swal.fire({ icon: "warning", text: "Référence obligatoire.", confirmButtonColor: "#2f4595" }); return; }
        if (!domaineForm.nom?.trim())       { Swal.fire({ icon: "warning", text: "Nom obligatoire.",       confirmButtonColor: "#2f4595" }); return; }

        const payload = {
            reference:   domaineForm.reference.trim(),
            nom:         domaineForm.nom.trim(),
            description: domaineForm.description?.trim() || "",
            couleur:     domaineForm.couleur || "#2f4595",
            actif:       !!domaineForm.actif,
            imageFile:   domaineForm.imageFile || null,
        };

        if (editingDomaine?.id) dispatch(updateDomaineRequest(editingDomaine.id, payload));
        else                    dispatch(createDomaineRequest(payload));
        closeDomaineModal();
    };

    const toggleActifDomaine = (d) => {
        dispatch(updateDomaineRequest(d.id, { ...d, actif: !d.actif }));
    };

    const confirmDeleteDomaine = (d) => {
        Swal.fire({
            title: "Supprimer", text: `Supprimer le domaine "${d.nom}" ?`,
            icon: "warning", showCancelButton: true,
            confirmButtonText: "Oui, supprimer", cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545", cancelButtonColor: "#2f4595",
        }).then((res) => { if (res.isConfirmed) dispatch(deleteDomaineRequest(d.id)); });
    };

    /* ── Handlers Thème ── */
    const openCreateTheme = () => {
        setEditingTheme(null);
        setThemeForm({ ...emptyThemeForm, domaineId: selectedDomaineId || "" });
        setOpenThemeModal(true);
    };

    const openEditTheme = (t) => {
        setEditingTheme(t);
        setThemeForm({
            reference:   t.reference  || "",
            nom:         t.nom        || "",
            description: t.description|| "",
            actif:       t.actif      ?? true,
            domaineId:   t.domaineId  ? String(t.domaineId) : "",
        });
        setOpenThemeModal(true);
    };

    const closeThemeModal = () => {
        setOpenThemeModal(false);
        setEditingTheme(null);
        setThemeForm(emptyThemeForm);
    };

    const onChangeTheme = (e) => {
        const { name, value, type, checked } = e.target;
        setThemeForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    };

    const submitTheme = (e) => {
        e.preventDefault();
        if (!themeForm.reference?.trim()) { Swal.fire({ icon: "warning", text: "Référence obligatoire.",            confirmButtonColor: "#2f4595" }); return; }
        if (!themeForm.nom?.trim())       { Swal.fire({ icon: "warning", text: "Nom obligatoire.",                  confirmButtonColor: "#2f4595" }); return; }
        if (!themeForm.domaineId)         { Swal.fire({ icon: "warning", text: "Veuillez sélectionner un domaine.", confirmButtonColor: "#2f4595" }); return; }

        const payload = {
            reference:   themeForm.reference.trim(),
            nom:         themeForm.nom.trim(),
            description: themeForm.description?.trim() || "",
            actif:       !!themeForm.actif,
            domaineId:   Number(themeForm.domaineId),
        };

        if (editingTheme?.id) dispatch(updateThemeRequest(editingTheme.id, payload));
        else                  dispatch(createThemeRequest(payload));
        closeThemeModal();
    };

    const toggleActifTheme = (t) => {
        dispatch(updateThemeRequest(t.id, { ...t, actif: !t.actif }));
    };

    const confirmDeleteTheme = (t) => {
        Swal.fire({
            title: "Supprimer", text: `Supprimer le thème "${t.nom}" ?`,
            icon: "warning", showCancelButton: true,
            confirmButtonText: "Oui, supprimer", cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545", cancelButtonColor: "#2f4595",
        }).then((res) => { if (res.isConfirmed) dispatch(deleteThemeRequest(t.id)); });
    };

    /* ── Bulk ── */
    const openBulk = (type) => {
        setBulkType(type);
        setBulkFile(null);
        setBulkFileName("");
        dispatch(clearBulkReview());
        setOpenBulkModal(true);
    };

    const closeBulk = () => {
        setOpenBulkModal(false);
        setBulkFile(null);
        setBulkFileName("");
        dispatch(clearBulkReview());
    };

    const downloadTemplate = () => {
        if (bulkType === "DOMAINES") dispatch(downloadDomainesTemplateRequest());
        else                         dispatch(downloadThemesTemplateRequest());
    };

    const runBulkImport = () => {
        if (!bulkFile) { Swal.fire({ icon: "warning", text: "Veuillez sélectionner un fichier Excel.", confirmButtonColor: "#2f4595" }); return; }
        if (bulkType === "DOMAINES") dispatch(importDomainesExcelRequest(bulkFile));
        else                         dispatch(importThemesExcelRequest(bulkFile));
    };

    const applyServerFilter = () => {
        if (!selectedDomaineId) dispatch(fetchThemesRequest());
        else                    dispatch(fetchThemesByDomaineRequest(selectedDomaineId));
    };

    /* ── Rendu ── */
    return (
        <div className="pdt-page">

            {/* ── Header ── */}
            <div className="pdt-header">
                <div>
                    <h1>Gestion des domaines & thèmes</h1>
                    {/*<p>Configurez les rubriques et thématiques de formation.</p>*/}
                    {/*{error && <div style={{ marginTop: 8, color: "#b91c1c", fontWeight: 700 }}>Erreur : {error}</div>}*/}
                </div>
            </div>

            {/* ── Card avec tabs ── */}
            <div className="pdt-wrap">
                <div className="pdt-card pdt-filter-card">

                    {/* Tabs */}
                    <div className="pdt-tabs">
                        {tabs.map((t) => {
                            const Icon = t.icon;
                            return (
                                <button
                                    key={t.id}
                                    className={`pdt-tab ${tab === t.id ? "is-active" : ""}`}
                                    onClick={() => { setTab(t.id); setQ(""); setSelectedDomaineId(""); }}
                                    type="button"
                                >
                                    <Icon size={15} />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="pdt-body">

                        {/* ═══ TAB DOMAINES ═══ */}
                        {tab === "domaines" && (
                            <div>
                                <div className="pdt-top-row">
                                    <div className="pdt-search-wrap">
                                        <Search size={14} className="pdt-search-icon" />
                                        <input
                                            className="pdt-search"
                                            placeholder="Référence, nom, description…"
                                            value={q}
                                            onChange={(e) => setQ(e.target.value)}
                                        />
                                    </div>
                                    <div className="pdt-top-actions">
                                        <button className="pdt-btn pdt-btn--ghost" type="button" onClick={() => openBulk("DOMAINES")} disabled={loading}>
                                            <Upload size={14} /> Import massif
                                        </button>
                                        <button className="pdt-btn pdt-btn--primary" type="button" onClick={openCreateDomaine} disabled={loading}>
                                            <Plus size={14} /> Ajouter
                                        </button>
                                    </div>
                                </div>

                                <div className="pdt-table-wrap">
                                    <table className="pdt-table">
                                        <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Nom</th>
                                            <th>Description</th>
                                            <th>Couleur</th>
                                            <th>Image</th>
                                            <th>Statut</th>
                                            <th className="is-right">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {loading ? (
                                            <tr><td colSpan={7} className="pdt-empty-cell">Chargement…</td></tr>
                                        ) : filteredDomaines.length === 0 ? (
                                            <tr><td colSpan={7} className="pdt-empty-cell">Aucun domaine configuré</td></tr>
                                        ) : filteredDomaines.map((d) => (
                                            <tr key={d.id}>
                                                <td>
                                                        <span className="pdt-code" style={{ backgroundColor: d.couleur || "#2f4595" }}>
                                                            {d.reference}
                                                        </span>
                                                </td>
                                                <td className="pdt-strong">{d.nom}</td>
                                                <td className="pdt-muted pdt-truncate">{d.description || "—"}</td>
                                                <td>
                                                    <div className="pdt-color-cell">
                                                        <span className="pdt-color-dot" style={{ background: d.couleur || "#2f4595" }} />
                                                        <span className="pdt-color-hex">{d.couleur || "#2f4595"}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    {d.image ? (
                                                        <img
                                                            className="pdt-thumb"
                                                            src={`${API_BASE_URL}/api/domaines/images/${d.image}`}
                                                            alt="img"
                                                            onError={(e) => (e.currentTarget.style.display = "none")}
                                                        />
                                                    ) : <span className="pdt-muted">—</span>}
                                                </td>
                                                <td>
                                                    <button
                                                        className={`pdt-pill ${d.actif ? "is-on" : "is-off"}`}
                                                        type="button"
                                                        onClick={() => toggleActifDomaine(d)}
                                                        disabled={loading}
                                                    >
                                                        {d.actif ? <CheckCircle size={12} /> : <XCircle size={12} />}
                                                        {d.actif ? "Actif" : "Inactif"}
                                                    </button>
                                                </td>
                                                <td className="is-right">
                                                    <div className="pdt-row-actions">
                                                        <button className="pdt-icon-btn" type="button" title="Modifier" onClick={() => openEditDomaine(d)} disabled={loading}>
                                                            <Edit2 size={15} />
                                                        </button>
                                                        <button className="pdt-icon-btn pdt-icon-btn--danger" type="button" title="Supprimer" onClick={() => confirmDeleteDomaine(d)} disabled={loading}>
                                                            <Trash2 size={15} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* ═══ TAB THÈMES ═══ */}
                        {tab === "themes" && (
                            <div>
                                <div className="pdt-top-row">
                                    <div className="pdt-search-wrap">
                                        <Search size={14} className="pdt-search-icon" />
                                        <input
                                            className="pdt-search"
                                            placeholder="Référence, thématique, domaine…"
                                            value={q}
                                            onChange={(e) => setQ(e.target.value)}
                                        />
                                    </div>
                                    <div className="pdt-top-actions">
                                        <select className="pdt-select" value={selectedDomaineId} onChange={(e) => setSelectedDomaineId(e.target.value)}>
                                            <option value="">— Tous les domaines —</option>
                                            {domaines.map((d) => (
                                                <option key={d.id} value={d.id}>{d.nom}</option>
                                            ))}
                                        </select>
                                        <button className="pdt-btn pdt-btn--ghost" type="button" onClick={applyServerFilter} disabled={loading}>
                                            <Filter size={14} /> Filtrer
                                        </button>
                                        <button className="pdt-btn pdt-btn--ghost" type="button" onClick={() => openBulk("THEMES")} disabled={loading}>
                                            <Upload size={14} /> Import massif
                                        </button>
                                        <button className="pdt-btn pdt-btn--primary" type="button" onClick={openCreateTheme} disabled={loading}>
                                            <Plus size={14} /> Ajouter
                                        </button>
                                    </div>
                                </div>

                                <div className="pdt-table-wrap">
                                    <table className="pdt-table">
                                        <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Nom</th>
                                            <th>Domaine rattaché</th>
                                            <th>Description</th>
                                            <th>Statut</th>
                                            <th className="is-right">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {loading ? (
                                            <tr><td colSpan={6} className="pdt-empty-cell">Chargement…</td></tr>
                                        ) : filteredThemes.length === 0 ? (
                                            <tr><td colSpan={6} className="pdt-empty-cell">Aucun thème configuré</td></tr>
                                        ) : filteredThemes.map((t) => (
                                            <tr key={t.id}>
                                                <td>
                                                        <span className="pdt-code" style={{ backgroundColor: "#2f4595" }}>
                                                            {t.reference}
                                                        </span>
                                                </td>
                                                <td className="pdt-strong">{t.nom}</td>
                                                <td>
                                                    <span className="pdt-badge">{t.domaineNom || "—"}</span>
                                                </td>
                                                <td className="pdt-muted pdt-truncate">{t.description || "—"}</td>
                                                <td>
                                                    <button
                                                        className={`pdt-pill ${t.actif ? "is-on" : "is-off"}`}
                                                        type="button"
                                                        onClick={() => toggleActifTheme(t)}
                                                        disabled={loading}
                                                    >
                                                        {t.actif ? <CheckCircle size={12} /> : <XCircle size={12} />}
                                                        {t.actif ? "Actif" : "Inactif"}
                                                    </button>
                                                </td>
                                                <td className="is-right">
                                                    <div className="pdt-row-actions">
                                                        <button className="pdt-icon-btn" type="button" title="Modifier" onClick={() => openEditTheme(t)} disabled={loading}>
                                                            <Edit2 size={15} />
                                                        </button>
                                                        <button className="pdt-icon-btn pdt-icon-btn--danger" type="button" title="Supprimer" onClick={() => confirmDeleteTheme(t)} disabled={loading}>
                                                            <Trash2 size={15} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════
                MODAL DOMAINE
            ═══════════════════════════════════ */}
            <Modal
                open={openDomaineModal}
                title={editingDomaine ? "Modifier un domaine" : "Ajouter un domaine"}
                onClose={closeDomaineModal}
                footer={
                    <>
                        <button className="pdt-btn pdt-btn--ghost" type="button" onClick={closeDomaineModal} disabled={loading}>Annuler</button>
                        <button className="pdt-btn pdt-btn--primary" type="button" onClick={submitDomaine} disabled={loading}>
                            {editingDomaine ? "Enregistrer" : "Ajouter"}
                        </button>
                    </>
                }
            >
                <div className="pdt-form">
                    <div className="pdt-field">
                        <label>Référence *</label>
                        <input type="text" name="reference" value={domaineForm.reference} onChange={onChangeDomaine} placeholder="ex : DOM-001" disabled={loading} />
                    </div>
                    <div className="pdt-field">
                        <label>Nom *</label>
                        <input type="text" name="nom" value={domaineForm.nom} onChange={onChangeDomaine} placeholder="Intitulé du domaine" disabled={loading} />
                    </div>
                    <div className="pdt-field pdt-field--full">
                        <label>Description</label>
                        <textarea name="description" value={domaineForm.description} onChange={onChangeDomaine} rows={3} placeholder="Détails…" disabled={loading} />
                    </div>
                    <div className="pdt-field">
                        <label>Couleur d'identification</label>
                        <div className="pdt-color-picker-row">
                            <input type="color" name="couleur" value={domaineForm.couleur} onChange={onChangeDomaine} disabled={loading} />
                            <span className="pdt-color-hex-label">{domaineForm.couleur}</span>
                        </div>
                    </div>
                    <div className="pdt-field">
                        <label>Image (optionnel)</label>
                        <input type="file" accept="image/*" onChange={onPickImage} disabled={loading} />
                        {imagePreview && (
                            <div className="pdt-preview">
                                <img src={imagePreview} alt="Aperçu" />
                            </div>
                        )}
                    </div>
                    <div className="pdt-field pdt-field--check-row">
                        <input type="checkbox" name="actif" checked={!!domaineForm.actif} onChange={onChangeDomaine} disabled={loading} />
                        <span>Domaine actif</span>
                    </div>
                </div>
            </Modal>

            {/* ═══════════════════════════════════
                MODAL THÈME
            ═══════════════════════════════════ */}
            <Modal
                open={openThemeModal}
                title={editingTheme ? "Modifier un thème" : "Ajouter un thème"}
                onClose={closeThemeModal}
                footer={
                    <>
                        <button className="pdt-btn pdt-btn--ghost" type="button" onClick={closeThemeModal} disabled={loading}>Annuler</button>
                        <button className="pdt-btn pdt-btn--primary" type="button" onClick={submitTheme} disabled={loading}>
                            {editingTheme ? "Enregistrer" : "Ajouter"}
                        </button>
                    </>
                }
            >
                <div className="pdt-form">
                    <div className="pdt-field">
                        <label>Référence *</label>
                        <input type="text" name="reference" value={themeForm.reference} onChange={onChangeTheme} placeholder="ex : THE-001" disabled={loading} />
                    </div>
                    <div className="pdt-field">
                        <label>Nom *</label>
                        <input type="text" name="nom" value={themeForm.nom} onChange={onChangeTheme} placeholder="Intitulé du thème" disabled={loading} />
                    </div>
                    <div className="pdt-field">
                        <label>Domaine de rattachement *</label>
                        <select name="domaineId" value={themeForm.domaineId} onChange={onChangeTheme} disabled={loading}>
                            <option value="">— Sélectionner —</option>
                            {domaines.map((d) => (
                                <option key={d.id} value={d.id}>{d.nom}</option>
                            ))}
                        </select>
                    </div>
                    <div className="pdt-field pdt-field--check-row">
                        <input type="checkbox" name="actif" checked={!!themeForm.actif} onChange={onChangeTheme} disabled={loading} />
                        <span>Thème actif</span>
                    </div>
                    <div className="pdt-field pdt-field--full">
                        <label>Description</label>
                        <textarea name="description" value={themeForm.description} onChange={onChangeTheme} rows={3} placeholder="Détails…" disabled={loading} />
                    </div>
                </div>
            </Modal>

            {/* ═══════════════════════════════════
                MODAL IMPORT MASSIF
            ═══════════════════════════════════ */}
            <Modal
                open={openBulkModal}
                title={`Import massif — ${bulkType === "DOMAINES" ? "Domaines" : "Thèmes"}`}
                onClose={closeBulk}
                footer={
                    <>
                        <button className="pdt-btn pdt-btn--ghost" type="button" onClick={downloadTemplate}>
                            <FileSpreadsheet size={14} /> Template Excel
                        </button>
                        <button className="pdt-btn pdt-btn--ghost" type="button" onClick={closeBulk}>Annuler</button>
                        <button className="pdt-btn pdt-btn--primary" type="button" onClick={runBulkImport} disabled={bulkLoading}>
                            <Upload size={14} /> {bulkLoading ? "Traitement…" : "Lancer l'import"}
                        </button>
                    </>
                }
            >
                <div className="pdt-form">
                    <div className="pdt-field pdt-field--full">
                        <label>Sélectionner le fichier (.xlsx, .xls)</label>
                        <label className={`pdt-file-label ${bulkFileName ? "has-file" : ""}`}>
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    const f = e.target.files?.[0] || null;
                                    setBulkFile(f);
                                    setBulkFileName(f ? f.name : "");
                                }}
                            />
                            <Upload size={14} />
                            <span>{bulkFileName || "Choisir un fichier…"}</span>
                        </label>
                    </div>
                </div>

                {bulkResult && (
                    <div className="pdt-import-result">
                        <div className="pdt-import-summary">
                            <div className="pdt-summary-item">Lignes : <b>{bulkResult.totalRows}</b></div>
                            <div className="pdt-summary-item">Succès : <b style={{ color: "#10b981" }}>{bulkResult.createdCount}</b></div>
                            <div className="pdt-summary-item">Ignorées : <b>{bulkResult.skippedCount}</b></div>
                            <div className="pdt-summary-item">Erreurs : <b style={{ color: "#dc3545" }}>{(bulkResult.errors || []).length}</b></div>
                        </div>

                        {(bulkResult.errors || []).length > 0 && (
                            <div className="pdt-table-wrap" style={{ maxHeight: 180, border: "1px solid #fee2e2", borderRadius: 8 }}>
                                <table className="pdt-table" style={{ fontSize: 11 }}>
                                    <thead>
                                    <tr>
                                        <th style={{ background: "#fef2f2", color: "#991b1b" }}>Ligne</th>
                                        <th style={{ background: "#fef2f2", color: "#991b1b" }}>Colonne</th>
                                        <th style={{ background: "#fef2f2", color: "#991b1b" }}>Message</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {bulkResult.errors.map((er, idx) => (
                                        <tr key={idx}>
                                            <td>{er.row}</td>
                                            <td style={{ fontWeight: 700 }}>{er.column}</td>
                                            <td style={{ color: "#991b1b" }}>{er.message}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
}
